import React, { useState, useEffect, Component } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { format, parseISO } from 'date-fns';
import { Search, Filter, Eye, X, AlertTriangle } from 'lucide-react';

const ACTION_COLORS = {
  'CREATE': 'bg-green-100 text-green-700',
  'UPDATE': 'bg-blue-100 text-blue-700',
  'DELETE': 'bg-red-100 text-red-700',
  'ALLOCATE': 'bg-purple-100 text-purple-700',
  'UNALLOCATE': 'bg-purple-100 text-purple-700',
  'IMPORT': 'bg-cyan-100 text-cyan-700',
  'EXPORT': 'bg-cyan-100 text-cyan-700',
  'PASSWORD': 'bg-amber-100 text-amber-700'
};

const getActionColor = (action) => {
  if (!action) return 'bg-gray-100 text-gray-700';
  for (const [key, color] of Object.entries(ACTION_COLORS)) {
    if (action.includes(key)) return color;
  }
  return 'bg-gray-100 text-gray-700';
};

// Error Boundary
class AuditLogsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Failed to load audit logs</p>
          <Button onClick={() => this.setState({ hasError: false })} className="mt-4">Retry</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AssetAuditLogs = () => {
  const { getAuditLogs, isReady } = useAsset();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    username: '',
    date_from: '',
    date_to: ''
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    if (isReady) {
      loadLogs();
    }
  }, [isReady]);

  useEffect(() => {
    if (!isReady) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadLogs(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filters, isReady]);

  const loadLogs = async (page = currentPage) => {
    setLoading(true);
    try {
      const filterParams = { ...filters };
      if (searchQuery) filterParams.search = searchQuery;
      
      const result = await getAuditLogs(page, 50, filterParams);
      
      if (result && Array.isArray(result.data)) {
        setLogs(result.data);
        setPagination(result.pagination || { page: 1, page_size: 50, total: result.data.length, total_pages: 1 });
      } else if (Array.isArray(result)) {
        setLogs(result);
        setPagination({ page: 1, page_size: result.length, total: result.length, total_pages: 1 });
      } else {
        setLogs([]);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setLogs([]);
    }
    setLoading(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadLogs(newPage);
  };

  const clearFilters = () => {
    setFilters({ action: '', username: '', date_from: '', date_to: '' });
    setSearchQuery('');
  };

  const hasActiveFilters = searchQuery || Object.values(filters).some(v => v);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(parseISO(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const openDetailDialog = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  return (
    <AuditLogsErrorBoundary>
      <div className="space-y-6" data-testid="asset-audit-logs">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Audit Logs</h2>
            <p className="text-muted-foreground">Track all actions performed in the system</p>
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="mr-2 h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>

        {/* Search & Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, action, entity type, or details..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Action</Label>
                  <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                    <SelectTrigger><SelectValue placeholder="All Actions" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Actions</SelectItem>
                      <SelectItem value="ASSET_CREATED">Asset Created</SelectItem>
                      <SelectItem value="ASSET_UPDATED">Asset Updated</SelectItem>
                      <SelectItem value="ASSET_DELETED">Asset Deleted</SelectItem>
                      <SelectItem value="ASSET_ALLOCATED">Asset Allocated</SelectItem>
                      <SelectItem value="ASSET_UNALLOCATED">Asset Unallocated</SelectItem>
                      <SelectItem value="EMPLOYEE_CREATED">Employee Created</SelectItem>
                      <SelectItem value="EMPLOYEE_UPDATED">Employee Updated</SelectItem>
                      <SelectItem value="EMPLOYEE_DELETED">Employee Deleted</SelectItem>
                      <SelectItem value="PROJECT_CREATED">Project Created</SelectItem>
                      <SelectItem value="ASSETS_IMPORTED">Assets Imported</SelectItem>
                      <SelectItem value="EMPLOYEES_IMPORTED">Employees Imported</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date From</Label>
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date To</Label>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
                </div>
                <div className="flex items-end">
                  {hasActiveFilters && (
                    <Button variant="ghost" onClick={clearFilters} className="gap-2">
                      <X className="h-4 w-4" /> Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Audit Trail</span>
              <Badge variant="secondary">{pagination?.total || 0} entries</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : !Array.isArray(logs) || logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No audit logs found
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead className="w-[120px]">User</TableHead>
                      <TableHead className="w-[180px]">Action</TableHead>
                      <TableHead className="w-[100px]">Entity</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <TableRow key={log?.id || index}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatTimestamp(log?.timestamp)}
                        </TableCell>
                        <TableCell className="font-medium">{log?.username || 'Unknown'}</TableCell>
                        <TableCell>
                          <Badge className={getActionColor(log?.action)}>
                            {log?.action || 'UNKNOWN'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log?.entity_type || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
                          {log?.details || ''}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => openDetailDialog(log)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {!loading && Array.isArray(logs) && logs.length > 0 && pagination?.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.total_pages}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-4 text-sm text-muted-foreground text-center">
              Logs are immutable and cannot be modified
            </div>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Audit Log Details</DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Timestamp</Label>
                    <p className="font-medium">{formatTimestamp(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">User</Label>
                    <p className="font-medium">{selectedLog.username}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Action</Label>
                    <Badge className={`mt-1 ${getActionColor(selectedLog.action)}`}>
                      {selectedLog.action}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Entity Type</Label>
                    <p className="font-medium">{selectedLog.entity_type}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Entity ID</Label>
                  <p className="font-mono text-sm">{selectedLog.entity_id || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Details</Label>
                  <p className="mt-1 p-3 bg-muted rounded-md text-sm">{selectedLog.details || 'No details available'}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AuditLogsErrorBoundary>
  );
};

export default AssetAuditLogs;
